/**
 * Business register module API client + types (A1, stage 1). Matches the
 * responses of apps/api/src/modules/ariregister/ariregister.routes.ts.
 */
import { api } from "./api";
import type { BrainBadge } from "./siteBrain";
import type { ArProcurementWin } from "./procurement";

export interface ArListRow {
  regCode: string;
  name: string;
  legalForm: string | null;
  legalFormSub: string | null;
  status: string | null;
  statusText: string | null;
  county: string | null;
  vatNumber: string | null;
  emtak: string | null;
  emtakText: string | null;
  addressFull: string | null;
  /** Share capital in cents - an optional table column, and a sort key. */
  capitalCents: number | null;
  /** Website from the register - null means no domain to check. */
  webUrl: string | null;
  /** Contact scalars from the register. The mirror publishes more channels in
   *  `commChannels` (detail only); these are the first of each kind, and the
   *  only ones the list's search can match. */
  email: string | null;
  phone: string | null;
  registeredAt: string | null;
  deletedAt: string | null;
  syncedAt: string;
  // Wealth signals (denormalized) - in cents.
  taxYear: number | null;
  taxRevenueCents: number | null;
  taxEmployees: number | null;
  reportYear: number | null;
  reportRevenueCents: number | null;
  reportProfitCents: number | null;
  reportEmployees: number | null;
  /** 0-100, how good a prospect this company is for web work. Null until the
   *  recompute pass has reached the row - never render that as a zero. See
   *  `lib/webNeed.ts`. */
  webNeedScore: number | null;
  /**
   * Tax arrears from EMTA's public debtors list, in cents.
   *
   * Null means NOT ON THE NEWEST LIST, which is not the same as "owes nothing":
   * it is also what every row says while the import has never run. Render it as
   * an empty cell, never as a zero and never as a clean bill of health.
   */
  debtCents: number | null;
  /** The debtors file's own as-of date, so the number can carry its age. */
  debtDataAt: string | null;
  /**
   * How many VALID activity licences the company holds (MTR).
   *
   * "Valid" is MTR's own status AND an end date that has not passed - the two
   * disagree on 4% of the file. Null means no valid licence in the newest MTR
   * file, which also covers every row while that import has never run.
   */
  licenceCount: number | null;
  /** The newest start date among them. A permit granted last month is a
   *  business that has just started doing something new. Can be in the FUTURE -
   *  90 licences in the file begin on a later date. */
  licenceNewestAt: string | null;
  /**
   * Whether the development brain has looked at this company's site (R6).
   *
   * Resolved SERVER-SIDE from `webUrl`, not derived here: the brain is keyed by
   * the registrable domain while `webUrl` is free text somebody typed into a
   * form, so a client-side key would silently lose every `www.` row's badge.
   * Null means no run - which is most rows, and is drawn as an empty cell.
   */
  brain: BrainBadge | null;
}

export interface ArListResult {
  rows: ArListRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface ArActivity {
  emtak_kood: string | null;
  emtak_tekstina: string | null;
  nace_kood: string | null;
  on_pohitegevusala: boolean;
}
export interface ArCommChannel {
  liik: string;
  sisu: string;
}
export interface ArPerson {
  id: string;
  personType: string | null;
  personName: string;
  personCodeHash: string | null;
  linkedRegCode: string | null;
  roleCode: string | null;
  role: string;
  birthDate: string | null;
  email: string | null;
  startDate: string | null;
  endDate: string | null;
  reprRights: string | null;
}

export interface ArShareholder {
  id: string;
  personType: string | null;
  ownerName: string;
  personCodeHash: string | null;
  linkedRegCode: string | null;
  role: string | null;
  birthDate: string | null;
  sharePercent: string | null;
  shareAmount: string | null;
  shareCurrency: string | null;
  ownershipType: string | null;
  startDate: string | null;
  endDate: string | null;
}
export interface ArBeneficial {
  id: string;
  ownerName: string;
  personCode: string | null;
  residenceCountry: string | null;
  controlType: string | null;
  verifiedVia: string | null;
}
export interface ArReport {
  id: string;
  year: number;
  reportId: number | null;
  revenueCents: number | null;
  operatingProfitCents: number | null;
  profitBeforeTaxCents: number | null;
  profitCents: number | null;
  laborCostCents: number | null;
  assetsCents: number | null;
  currentAssetsCents: number | null;
  cashCents: number | null;
  equityCents: number | null;
  currentLiabilitiesCents: number | null;
  nonCurrentLiabilitiesCents: number | null;
  employees: number | null;
  isConsolidated: boolean | null;
  isAudited: boolean | null;
  category: string | null;
  periodEnd: string | null;
  submittedAt: string | null;
}

/** EMTA quarterly cell (ArTaxData.quarters JSON; cents as a string). */
export interface ArQuarter {
  q: number;
  revenueCents: string | null;
  stateTaxCents: string | null;
  laborTaxCents: string | null;
  employees: number | null;
}
export interface ArTaxData {
  id: string;
  year: number;
  dataDate: string | null;
  revenueCents: number | null;
  stateTaxCents: number | null;
  laborTaxCents: number | null;
  employees: number | null;
  quarters: ArQuarter[] | null;
}
/**
 * One ruling on the company's registry file (RIK "maarused").
 *
 * The `*Text` fields are the register's own wording; the bare codes next to them
 * are kept because a text is occasionally missing while the code is not.
 */
export interface ArRuling {
  id: string;
  rulingNo: string;
  rulingDate: string | null;
  entryDate: string | null;
  /** Deadline to cure defects - only defect rulings carry one. */
  extraDeadline: string | null;
  kind: string | null;
  kindText: string | null;
  state: string | null;
  stateText: string | null;
  entryKind: string | null;
  entryKindText: string | null;
  effectiveAt: string | null;
  effectState: string | null;
  effectStateText: string | null;
}

export interface ArReportActivity {
  id: string;
  year: number;
  emtak: string;
  emtakVersion: string | null;
  revenueCents: number | null;
  isMain: boolean;
}

export interface ArDetail extends ArListRow {
  commChannels: ArCommChannel[] | null;
  activities: ArActivity[] | null;
  capitalCurrency: string | null;
  addressEhak: string | null;
  zip: string | null;
  sourceUpdatedAt: string | null;
  detailFetchedAt: string | null;
  // Denormalized wealth (the detail also carries the tax/labor split).
  taxStateTaxCents: number | null;
  taxLaborTaxCents: number | null;
  taxUpdatedAt: string | null;
  reportEquityCents: number | null;
  reportAssetsCents: number | null;
  reportUpdatedAt: string | null;
  // The arrears breakdown. The list row already carries the total and its date.
  debtDisputedCents: number | null;
  debtScheduledCents: number | null;
  debtScheduleEndAt: string | null;
  debtOldestDueAt: string | null;
  debtUpdatedAt: string | null;
  licenceUpdatedAt: string | null;
  licences: ArLicence[];
  /** Public procurement wins (RHR award notices, since 12.05.2023). The count is
   *  procurements, the sum every winning tender; null = no win in the mirror,
   *  which is also every company while the import has never run. */
  procurementWinCount: number | null;
  procurementWinCents: number | null;
  procurementLastWonAt: string | null;
  procurementUpdatedAt: string | null;
  procurementWins: ArProcurementWin[];
  persons: ArPerson[];
  shareholders: ArShareholder[];
  beneficials: ArBeneficial[];
  reports: ArReport[];
  taxData: ArTaxData[];
  reportActivities: ArReportActivity[];
  rulings: ArRuling[];
}

/** One activity licence or notice of economic activity (MTR). */
export interface ArLicence {
  id: string;
  /** MTR's registry number. Unique within a company, not across the register. */
  number: string;
  /** "Tegevusluba" (a licence) or "Majandustegevusteade" (a notice). */
  kind: string;
  /** "kehtiv" | "esitatud" | "peatatud" - MTR's own word, untranslated, because
   *  the register prints it that way and an operator cross-checking against
   *  mtr.ttja.ee has to find the same word on both screens. */
  status: string;
  /** valdkond - the supervisory area. Empty on roughly a fifth of the file. */
  area: string | null;
  /** tegevusala - what may actually be done under it. The useful half. */
  activity: string | null;
  url: string | null;
  emtak: string[] | null;
  emtak2025: string[] | null;
  validFrom: string | null;
  validUntil: string | null;
}

export interface ArSyncRun {
  id: string;
  dataset: string;
  mode: string;
  status: string;
  fileDate: string | null;
  rowsSeen: number;
  rowsUpserted: number;
  rowsDeleted: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface ArSyncStatus {
  runs: ArSyncRun[];
  running: { id: string; dataset: string; mode: string; startedAt: string; rowsSeen: number; rowsUpserted: number } | null;
  stats: { entityCount: number; activeCount: number; deletedCount: number };
}

/** A full sync imports basic→general→persons→shareholders→rulings in order.
 *  tax = EMTA quarterly data, tax-history = the EMTA archive years,
 *  debt = the EMTA arrears snapshot;
 *  reports = annual reports (wealth);
 *  procurements = the public procurement register's award notices. */
export type SyncDataset =
  | "all" | "basic" | "general" | "persons" | "shareholders" | "rulings"
  | "tax" | "tax-history" | "reports" | "debt" | "procurements";

export interface ArListParams {
  q?: string;
  status?: string;
  county?: string;
  legalForm?: string;
  /** EMTAK activity - comma-separated code prefixes (see lib/emtak.ts). */
  emtak?: string;
  hasVat?: string;
  /** "true" = has a public e-mail; "false" = none. */
  hasEmail?: string;
  /** "true" = has a phone; "false" = none. */
  hasPhone?: string;
  /** "true" = the register has a website; "false" = none (sales lead). */
  hasWebsite?: string;
  /** Capital in euros (the backend converts it to cents). */
  capitalMin?: string;
  capitalMax?: string;
  /** Incorporation date range (ISO). */
  registeredFrom?: string;
  registeredTo?: string;
  includeDeleted?: string;
  /** Wealth filters. Revenue (EMTA) in euros; the backend converts to cents. */
  revenueMin?: string;
  revenueMax?: string;
  employeesMin?: string;
  employeesMax?: string;
  /** "true" = profitable (report net profit > 0); "false" = at a loss. */
  profitable?: string;
  /** "true" = has an annual report. */
  hasReport?: string;
  /** "true" = on EMTA's newest debtors list; "false" = not on it. "false" is
   *  the useful one ("do not spend a call here"), and it means exactly what it
   *  says - not on the list - which also covers companies the import has never
   *  reached. */
  hasDebt?: string;
  /** Arrears floor in euros; the backend converts to cents. Filters out the
   *  40-euro late declarations that are not a solvency signal. */
  debtMin?: string;
  /** "true" = holds at least one valid activity licence (MTR). */
  hasLicence?: string;
  /** ISO date - "granted a licence on or after this". The timing filter. */
  licenceSince?: string;
  /** "true" = has won a public procurement since 12.05.2023 (RHR). "false" = no
   *  win in the mirror, which also covers every contract below the threshold. */
  wonProcurement?: string;
  /** ISO date - "won a procurement on or after this", read on the newest win. */
  procurementSince?: string;
  /** Web need score bounds, 0-100 inclusive. Both exclude rows the recompute
   *  pass has never reached: a null score is "not computed", not zero. */
  webNeedMin?: string;
  webNeedMax?: string;
  sort?: ArSort;
  order?: ArOrder;
  page?: number;
  pageSize?: number;
}

/**
 * What the list can be ordered by - the same enum the API accepts
 * (`LIST_SORTS` in ariregister.routes.ts), and nothing else.
 *
 * The two lists are short and have to stay in step: every key here is an
 * INDEXED column on a ~376 000 row mirror, so a key the API does not know is a
 * 400, and a column that is not in the API's list would be a full sort on every
 * page turn. `columns.tsx` binds them to table columns.
 */
export type ArSort =
  | "name" | "regCode" | "registered" | "status" | "legalForm" | "county" | "vat"
  | "activity" | "capital" | "revenue" | "employees" | "reportRevenue" | "profit"
  | "webNeed" | "debt" | "licences" | "licenceNew";

export type ArOrder = "asc" | "desc";

export function listEntities(params: ArListParams): Promise<ArListResult> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
  }
  return api.get<ArListResult>(`/admin/ariregister?${qs.toString()}`);
}

export const getEntity = (regCode: string): Promise<ArDetail> =>
  api.get<ArDetail>(`/admin/ariregister/${encodeURIComponent(regCode)}`);

export interface ArAutocompleteItem {
  regCode: string;
  name: string;
  vatNumber: string | null;
  status: string | null;
}
export const autocomplete = (q: string): Promise<{ items: ArAutocompleteItem[] }> =>
  api.get<{ items: ArAutocompleteItem[] }>(`/admin/ariregister/autocomplete?q=${encodeURIComponent(q)}`);

export const getSyncStatus = (): Promise<ArSyncStatus> => api.get<ArSyncStatus>(`/admin/ariregister/sync/status`);

/* ── Statistics - register-wide aggregate figures (charts) ─────────────────── */

export interface ArStats {
  /** All records in the register. */
  total: number;
  /** Active (not deleted). */
  active: number;
  /** KMKR-liable (active). */
  vat: number;
  /** With a public e-mail (active). */
  email: number;
  /** With an annual report (net profit known). */
  withReport: number;
  /** Profit/loss split by the net profit of the latest report. */
  profit: { profit: number; loss: number; zero: number; noReport: number };
  /** Legal forms in descending order. */
  legalForms: { legalForm: string | null; count: number }[];
  /** Statuses (R/L/N) in descending order. */
  statuses: { status: string | null; statusText: string | null; count: number }[];
  /** Counties (active) in descending order; county may be null. */
  counties: { county: string | null; count: number }[];
  /** Web presence - active companies that have a website URL (in the register). */
  web: { withSite: number };
  /** Sales targeting - qualified pools of leads without a website. */
  prospects: { noSiteEmail: number; noSiteEmailRevenue: number; newLast12mo: number; newLast12moNoSite: number };
  /** Revenue bands (EMTA). band: none|lt50k|b50_200|b200_1m|gte1m. */
  revenueBands: { band: string; count: number }[];
  /** New companies by month (last 24 months). month: 'YYYY-MM'. */
  newByMonth: { month: string; count: number }[];
}

export const getStats = (): Promise<ArStats> => api.get<ArStats>(`/admin/ariregister/stats`);

/** County deep dive - profit/loss + activities (2-digit EMTAK division). */
export interface ArCountyStats {
  county: string | null;
  total: number;
  withReport: number;
  /** Web presence + newly created companies (sales targeting) in this county. */
  withSite: number;
  newLast12mo: number;
  profit: { profit: number; loss: number; zero: number; noReport: number };
  /** Activity divisions in descending order (div = 2-digit EMTAK prefix). */
  divisions: { div: string; count: number }[];
}

/** Sentinel for records with no county (county IS NULL). */
export const COUNTY_NULL = "__null__";

export const getCountyStats = (county: string): Promise<ArCountyStats> =>
  api.get<ArCountyStats>(`/admin/ariregister/stats/county?county=${encodeURIComponent(county)}`);

/** Legal form full name → abbreviation for chart bars (unknown → full name). */
export const LEGAL_FORM_SHORT: Record<string, string> = {
  Osaühing: "OÜ",
  Korteriühistu: "KÜ",
  "Füüsilisest isikust ettevõtja": "FIE",
  Mittetulundusühing: "MTÜ",
  Usaldusühing: "UÜ",
  Aktsiaselts: "AS",
  "Kohaliku omavalitsuse asutus": "KOV asutus",
  Tulundusühistu: "TÜH",
  Täisühing: "TÜ",
  Sihtasutus: "SA",
  "Välismaa äriühingu filiaal": "Filiaal",
  Ametiühing: "Ametiühing",
};
export const legalFormShort = (form: string | null): string =>
  form ? (LEGAL_FORM_SHORT[form] ?? form) : "Määramata";

export const triggerSync = (body: { dataset: SyncDataset; mode?: "bulk"; tombstone?: boolean }): Promise<unknown> =>
  api.post(`/admin/ariregister/sync`, body);

export const refreshDetail = (regCode: string): Promise<unknown> =>
  api.post(`/admin/ariregister/${encodeURIComponent(regCode)}/detail`);

/* ── Cross-ownership: person search + person → all companies ───────────────── */

export interface ArPersonHit {
  hash: string;
  name: string;
  companies: number;
  isBoard: boolean;
  isOwner: boolean;
}
export const searchPersons = (q: string): Promise<{ items: ArPersonHit[] }> =>
  api.get<{ items: ArPersonHit[] }>(`/admin/ariregister/persons?q=${encodeURIComponent(q)}`);

export interface ArPersonCompany {
  regCode: string;
  name: string;
  status: string | null;
  statusText: string | null;
  county: string | null;
}
export interface ArPersonView {
  hash: string;
  name: string;
  personType: string | null;
  board: (ArPerson & { company: ArPersonCompany })[];
  shares: (ArShareholder & { company: ArPersonCompany })[];
}
export const getPerson = (hash: string): Promise<ArPersonView> =>
  api.get<ArPersonView>(`/admin/ariregister/person?hash=${encodeURIComponent(hash)}`);

/** Cents → compact € string ("1,2 mln €" / "345 tuh €" / "1 234 €"). */
export function formatEurShort(cents: number | null | undefined): string {
  if (cents == null) return "-";
  const eur = cents / 100;
  const sign = eur < 0 ? "−" : "";
  const a = Math.abs(eur);
  if (a >= 1_000_000) return `${sign}${(a / 1_000_000).toLocaleString("et-EE", { maximumFractionDigits: 1 })} mln €`;
  if (a >= 10_000) return `${sign}${(a / 1_000).toLocaleString("et-EE", { maximumFractionDigits: 0 })} tuh €`;
  return `${sign}${a.toLocaleString("et-EE", { maximumFractionDigits: 0 })} €`;
}

/** Cents → full € ("1 234 567 €"), for the tooltip/detail view. */
export function formatEurFull(cents: number | null | undefined): string {
  if (cents == null) return "-";
  return `${(cents / 100).toLocaleString("et-EE", { maximumFractionDigits: 0 })} €`;
}

/** Status code → Badge variant (R registered / L in liquidation / K deleted). */
export function statusVariant(status: string | null): "default" | "secondary" | "destructive" | "outline" {
  if (status === "R") return "default";
  if (status === "L") return "secondary";
  if (status === "K" || status === "N") return "destructive";
  return "outline";
}

/** Estonian counties (open-data "X maakond" form) - county filter options. */
export const COUNTIES = [
  "Harju maakond",
  "Hiiu maakond",
  "Ida-Viru maakond",
  "Jõgeva maakond",
  "Järva maakond",
  "Lääne maakond",
  "Lääne-Viru maakond",
  "Põlva maakond",
  "Pärnu maakond",
  "Rapla maakond",
  "Saare maakond",
  "Tartu maakond",
  "Valga maakond",
  "Viljandi maakond",
  "Võru maakond",
];

/** Legal forms - legalForm filter options (open-data text form,
 *  ordered by how often they occur in the register). */
export const LEGAL_FORMS = [
  "Osaühing",
  "Korteriühistu",
  "Füüsilisest isikust ettevõtja",
  "Mittetulundusühing",
  "Usaldusühing",
  "Aktsiaselts",
  "Kohaliku omavalitsuse asutus",
  "Tulundusühistu",
  "Täisühing",
  "Sihtasutus",
  "Välismaa äriühingu filiaal",
  "Ametiühing",
];
